import React, { useEffect } from 'react';

import arrayMove from 'array-move';
import AvailableSequence from 'containers/AvailableSequence';
import { useTranslation } from 'react-i18next';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { v4 as uuidv4 } from 'uuid';

import usePrevious from 'components/hooks/usePrevious';

import { collectionsDiffer } from 'components/helpers/objects';
import { resourceIdsAsPositionParams } from 'components/helpers/ordering';

const SortableItem = SortableElement(
  ({ combinedSequence, containerRef, projectTradeId }) => (
    <AvailableSequence
      containerRef={containerRef}
      masterSequenceId={combinedSequence.masterSequenceId}
      projectTradeId={projectTradeId}
      sequenceId={combinedSequence.sequenceId}
    />
  ),
);

const SortableList = SortableContainer(
  ({ containerRef, isSorting, items, projectTradeId }) => (
    <ul className='block-list block-list--border-bottom-none tw-relative'>
      {items.map((combinedSequence, index) => (
        <React.Fragment key={`sequence--${index}`}>
          {isSorting && (
            <div className='row-bar--ghost row-bar__ghost--sequence tw-border-blue-100 tw-bg-blue-025' />
          )}
          <SortableItem
            combinedSequence={combinedSequence}
            containerRef={containerRef}
            index={index}
            projectTradeId={projectTradeId}
          />
        </React.Fragment>
      ))}
    </ul>
  ),
);

const collapsedItemSpace = 52;

export default function SequenceList({
  addError,
  combinedSequences,
  containerRef,
  dispatch,
  projectId,
  projectTradeId,
  sequenceListIsOrdering,
  tradeId,
}) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const sequencePositionParams = resourceIdsAsPositionParams(
    combinedSequences.map((combinedSequence) => combinedSequence.sequenceId),
  );

  const wasPreviouslyMounted = usePrevious(true);
  const previousSequencePositionParams = usePrevious(sequencePositionParams);

  useEffect(() => {
    const shouldReorder =
      !!wasPreviouslyMounted &&
      Object.keys(sequencePositionParams).length > 0 &&
      collectionsDiffer(previousSequencePositionParams, sequencePositionParams);

    if (shouldReorder) requestMethodSequenceReorder();
  }, [
    wasPreviouslyMounted,
    previousSequencePositionParams,
    sequencePositionParams,
  ]);

  const requestMethodSequenceReorder = () => {
    axios
      .post(
        `/projects/${projectId}/method_sequences_bulk_updates`,
        {
          method_sequences: {
            method_sequences_attributes: sequencePositionParams,
          },
        },
        { params: { trade_id: tradeId } },
      )
      .catch(() =>
        addError(uuidv4(), { fullMessage: i18nTShared('errors.generic') }),
      );
  };

  const handleSequenceOrder = ({ newIndex, oldIndex }) => {
    const newlyOrderedCombinedSequences = arrayMove(
      combinedSequences,
      oldIndex,
      newIndex,
    );
    dispatch({
      type: 'FINISH_SEQUENCE_ORDERING',
      projectTradeId,
      newOrdering: newlyOrderedCombinedSequences,
    });
  };

  const handleSortStart = ({ index, node }) => {
    dispatch({
      type: 'START_SEQUENCE_ORDERING',
      projectTradeId,
      options: { containerRef, node, index, collapsedItemSpace },
    });
  };

  return (
    <SortableList
      containerRef={containerRef}
      getContainer={() => document.getElementById('main')}
      helperClass='sortable-item--active'
      isSorting={sequenceListIsOrdering}
      items={combinedSequences}
      lockAxis='y'
      lockToContainerEdges={true}
      onSortEnd={handleSequenceOrder}
      projectTradeId={projectTradeId}
      updateBeforeSortStart={handleSortStart}
      useDragHandle={true}
    />
  );
}
